import {
  buttonClass,
  Container,
  cx,
  moduleNav,
  PublicFooter,
  SiteHeader,
  Theme,
  useSitePartnerLogos,
} from '@growup/ui';
import { portalMeSchema } from '@growup/streaming-shared';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { getJson, signOut } from './api';
import { Apply } from './Apply';
import { Dashboard } from './Dashboard';
import { Home } from './Home';
import { PortalProvider, siteLink, usePortal } from './portal';
import { RequireSession, SessionProvider, signInPath, useSession } from './session';
import { SignIn } from './SignIn';

/**
 * streaming.growup.gg: the Grow uP streamer program. Signs in with the central
 * Grow uP account; creating it and linking Twitch happen on the main site.
 */
export function App() {
  return (
    <SessionProvider>
      <PortalProvider>
        <Theme name="dark" className="flex min-h-screen flex-col font-body">
          <Header />
          <div className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/entrar" element={<SignIn />} />
              <Route
                path="/candidatura"
                element={
                  <RequireSession>
                    <Page>
                      <Apply />
                    </Page>
                  </RequireSession>
                }
              />
              <Route
                path="/painel"
                element={
                  <RequireSession>
                    <Page>
                      <Dashboard />
                    </Page>
                  </RequireSession>
                }
              />
              <Route
                path="*"
                element={
                  <Page>
                    <p className="text-body">
                      Esta página não existe.{' '}
                      <Link className="gu-link" to="/">
                        Voltar ao início
                      </Link>
                    </p>
                  </Page>
                }
              />
            </Routes>
          </div>
          <Footer />
        </Theme>
      </PortalProvider>
    </SessionProvider>
  );
}

/** O rodape publico, com os parceiros do site (lidos da API do site). */
function Footer() {
  const logos = useSitePartnerLogos();
  return <PublicFooter current="streaming" partners={logos.length > 0} partnerLogos={logos} />;
}

function Page({ children }: { children: ReactNode }) {
  return <Container className="py-12">{children}</Container>;
}

/**
 * O menu conforme a pessoa (mapa do site, parte 4): quem ja e streamer ve o
 * Painel e nao a Candidatura; quem nao e ve a Candidatura e nao o Painel. Sem
 * sessao (ou enquanto se sabe), os tres. Os streamers publicos estao no site
 * (growup.gg/streamers), pelo seletor.
 */
function portalNav(streamer: boolean | null) {
  return [
    { label: 'O programa', href: '/' },
    ...(streamer === true ? [] : [{ label: 'Candidatura', href: '/candidatura' }]),
    ...(streamer === false ? [] : [{ label: 'Painel', href: '/painel' }]),
  ];
}

/** Se a conta com sessao e streamer da Grow uP (null sem sessao ou a carregar). */
function useIsStreamer(): boolean | null {
  const { session } = useSession();
  const accountId = session?.accountId;
  const [streamer, setStreamer] = useState<boolean | null>(null);
  useEffect(() => {
    setStreamer(null);
    if (!accountId) return;
    let alive = true;
    getJson('/api/me', portalMeSchema)
      .then((me) => {
        if (alive) setStreamer(me.streamer !== null);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [accountId]);
  return streamer;
}

function Header() {
  const { pathname } = useLocation();
  const { session, restoring } = useSession();
  const streamer = useIsStreamer();
  const signedOut = !session && !restoring && pathname !== '/entrar';
  return (
    <SiteHeader
      product="Streaming"
      modules={moduleNav('streaming', undefined, { gestao: session?.staff ?? false })}
      nav={portalNav(streamer).map((item) => ({ ...item, current: pathname === item.href }))}
      actions={<AccountAction />}
      menuActions={signedOut ? <SignedOutActions pathname={pathname} inMenu /> : undefined}
    />
  );
}

function AccountAction() {
  const { session, restoring } = useSession();
  const { pathname } = useLocation();
  if (restoring) return null;
  if (session) {
    return (
      <button
        type="button"
        className={buttonClass({ variant: 'ghost', size: 'sm' })}
        onClick={() => void signOut()}
      >
        {/* Um login comprido nao empurra o cabecalho para fora do ecra. */}
        <span className="max-w-[9rem] truncate sm:max-w-none">{session.login}</span> · Sair
      </button>
    );
  }
  return <SignedOutActions pathname={pathname} />;
}

/**
 * Sem sessao: «Entrar» e, em destaque, «Criar conta», que abre o registo no
 * site principal (no telemovel «Entrar» vai para o Menu).
 */
function SignedOutActions({ pathname, inMenu = false }: { pathname: string; inMenu?: boolean }) {
  const signUp = siteLink(usePortal(), '/conta/registar');
  const signIn =
    pathname === '/entrar' ? null : (
      <Link
        to={signInPath(pathname === '/' ? '/painel' : pathname)}
        className={cx(
          buttonClass({ variant: inMenu ? 'secondary' : 'ghost', size: 'sm' }),
          'whitespace-nowrap',
          !inMenu && 'hidden sm:inline-flex',
        )}
      >
        Entrar
      </Link>
    );
  if (inMenu) return signIn;
  return (
    <>
      {signIn}
      {signUp ? (
        <a href={signUp} className={cx(buttonClass({ size: 'sm' }), 'whitespace-nowrap')}>
          Criar conta
        </a>
      ) : null}
    </>
  );
}
